import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Icone } from '../components/Icone';
import { IconePleine } from '../components/IconePleine';
import { BadgePresence } from '../components/messagerie/BadgePresence';
import { etatPresence, LIBELLE_PRESENCE } from '../components/messagerie/presence';
import { Modale } from '../components/ui/Modale';
import { MessageErreur } from '../components/ui/Primitives';
import { useToast } from '../components/ui/useToast';
import { ErreurApi } from '../lib/api';
import { gradientPour } from '../lib/avatar';
import { useAppel } from '../appels/useAppel';
import type { TypeAppel } from '../appels/types';
import {
  useConversations,
  useContacts,
  useCreerConversation,
  useEnvoyerMessage,
  useMessages,
  urlPieceJointeMessage,
  type AppelMessage,
  type ContactMessagerie,
  type ConversationResume,
  type PieceJointeMessage,
} from '../lib/useMessagerie';

/** Formats acceptés pour une pièce jointe, alignés sur `lib/storage.ts` côté API. */
const ACCEPT_PIECE_JOINTE =
  '.pdf,.jpg,.jpeg,.png,.mp4,.webm,application/pdf,image/jpeg,image/png,video/mp4,video/webm';
const TAILLE_MAX_PIECE_JOINTE_OCTETS = 40 * 1024 * 1024;

function tailleLisible(octets: number): string {
  if (octets < 1024) return `${octets} o`;
  if (octets < 1024 * 1024) return `${Math.round(octets / 1024)} Ko`;
  return `${(octets / (1024 * 1024)).toFixed(1)} Mo`;
}

function estImage(typeMime: string): boolean {
  return typeMime.startsWith('image/');
}

function estVideo(typeMime: string): boolean {
  return typeMime.startsWith('video/');
}

const FUSEAU = 'Indian/Reunion';

function heure(iso: string): string {
  return new Date(iso).toLocaleTimeString('fr-FR', { timeZone: FUSEAU, hour: '2-digit', minute: '2-digit' });
}

function dateEtHeureCourtes(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', {
    timeZone: FUSEAU,
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

function jourEnFrancais(iso: string): string {
  const date = new Date(iso);
  const aujourdHui = new Date();
  const cleJour = (valeur: Date): string =>
    valeur.toLocaleDateString('fr-FR', { timeZone: FUSEAU, year: 'numeric', month: '2-digit', day: '2-digit' });

  if (cleJour(date) === cleJour(aujourdHui)) return "Aujourd'hui";
  const hier = new Date(aujourdHui);
  hier.setDate(hier.getDate() - 1);
  if (cleJour(date) === cleJour(hier)) return 'Hier';
  return date.toLocaleDateString('fr-FR', { timeZone: FUSEAU, day: 'numeric', month: 'long' });
}

function messageErreur(erreur: unknown, repli: string): string {
  return erreur instanceof ErreurApi ? erreur.message : repli;
}

function contactAppelable(contact: ContactMessagerie): boolean {
  return etatPresence(contact.presence) === 'actif';
}

function dureeLisible(secondes: number): string {
  const min = Math.floor(secondes / 60);
  const sec = secondes % 60;
  return min > 0 ? `${min} min ${String(sec).padStart(2, '0')} s` : `${sec} s`;
}

/** Libellé du log d'appel affiché dans le fil, à la Discord : jamais la valeur brute du statut. */
function libelleAppel(appel: AppelMessage, deMoi: boolean): string {
  const nature = appel.type === 'audio' ? 'audio' : 'vidéo';
  switch (appel.statut) {
    case 'TERMINE':
      return `Appel ${nature} · ${dureeLisible(appel.dureeSecondes ?? 0)}`;
    case 'MANQUE':
      return deMoi ? `Appel ${nature} sans réponse` : `Appel ${nature} manqué`;
    case 'REFUSE':
      return `Appel ${nature} refusé`;
    case 'ECHOUE':
      return `Appel ${nature} échoué`;
    case 'INDISPONIBLE':
      return 'Appel impossible pour le moment. Réessayez lorsque cet utilisateur sera disponible.';
  }
}

export function MessagesPage(): JSX.Element {
  const toast = useToast();
  const { appeler } = useAppel();
  const [searchParams] = useSearchParams();
  const [conversationActiveId, setConversationActiveId] = useState<string | null>(null);
  const [filtre, setFiltre] = useState<'tous' | 'non_lus'>('tous');
  const [recherche, setRecherche] = useState('');
  const [texteMessage, setTexteMessage] = useState('');
  const [pickerOuvert, setPickerOuvert] = useState(false);
  const [rechercheContact, setRechercheContact] = useState('');
  const [fichierSelectionne, setFichierSelectionne] = useState<File | null>(null);
  /** Envoi refusé (mot bloqué, TF-484, ou autre) : affiché sous la saisie jusqu'à la prochaine frappe. */
  const [refusEnvoi, setRefusEnvoi] = useState<string | null>(null);
  const inputFichierRef = useRef<HTMLInputElement>(null);
  const finDesMessagesRef = useRef<HTMLDivElement>(null);
  const [pieceJointeAgrandie, setPieceJointeAgrandie] = useState<{
    pieceJointe: PieceJointeMessage;
    url: string;
    urlApercu: string;
  } | null>(null);

  const { data: donneesConversations, isLoading: chargementConversations } = useConversations();
  const conversations = useMemo(
    () => donneesConversations?.conversations ?? [],
    [donneesConversations],
  );

  const { data: donneesContacts } = useContacts();
  const contacts = donneesContacts?.contacts ?? [];

  const creerConversation = useCreerConversation();

  // Ouverture depuis « Mes interlocuteurs » : `?contact=<utilisateurId>` ouvre
  // le fil avec cette personne, et le crée s'il n'existe pas encore. La
  // création côté API est idempotente : un fil déjà ouvert est simplement
  // renvoyé. Tenté une seule fois, sinon un échec relancerait la mutation à
  // chaque rendu.
  const contactDemande = searchParams.get('contact');
  const contactTraiteRef = useRef<string | null>(null);
  useEffect(() => {
    if (!contactDemande || contactTraiteRef.current === contactDemande) return;
    contactTraiteRef.current = contactDemande;
    const existante = conversations.find((c) => c.contact?.id === contactDemande);
    if (existante) {
      setConversationActiveId(existante.id);
      return;
    }
    creerConversation.mutate(contactDemande, {
      onSuccess: (reponse) => setConversationActiveId(reponse.id),
      onError: (erreur) => toast(messageErreur(erreur, 'Impossible de démarrer la conversation')),
    });
  }, [contactDemande, conversations, creerConversation, toast]);

  useEffect(() => {
    if (conversationActiveId || conversations.length === 0) return;
    // Un contact demandé dans l'URL prime : ne pas ouvrir le premier fil de la
    // liste pendant que le sien se crée.
    if (contactDemande) return;
    // Ouverture depuis la pop-up de réponse rapide : `?conversation=<id>`
    // sélectionne ce fil précis plutôt que le premier de la liste.
    const depuisUrl = searchParams.get('conversation');
    if (depuisUrl && conversations.some((c) => c.id === depuisUrl)) {
      setConversationActiveId(depuisUrl);
      return;
    }
    setConversationActiveId(conversations[0]!.id);
  }, [conversations, conversationActiveId, contactDemande, searchParams]);

  const conversationActive = conversations.find((c) => c.id === conversationActiveId) ?? null;

  const { data: donneesMessages, isLoading: chargementMessages } = useMessages(conversationActiveId);
  const messages = donneesMessages?.messages ?? [];

  const envoyerMessage = useEnvoyerMessage(conversationActiveId);

  // Fil ouvert au moment où la réponse revient : un texte refusé n'est rendu
  // qu'à son fil, jamais à celui d'une autre personne ouvert entre-temps.
  const filOuvertRef = useRef(conversationActiveId);
  useEffect(() => {
    filOuvertRef.current = conversationActiveId;
    setRefusEnvoi(null);
  }, [conversationActiveId]);

  // Toujours faire défiler vers le dernier message : au changement de fil,
  // à l'arrivée d'un nouveau message et après l'envoi du sien.
  useEffect(() => {
    finDesMessagesRef.current?.scrollIntoView({ block: 'end' });
  }, [conversationActiveId, messages.length]);

  const nonLuesCount = conversations.filter((c) => c.nonLus > 0).length;

  const conversationsFiltrees = useMemo(() => {
    return conversations.filter((conversation) => {
      if (filtre === 'non_lus' && conversation.nonLus === 0) return false;
      if (recherche.trim()) {
        const q = recherche.trim().toLowerCase();
        const nomContact = conversation.contact
          ? `${conversation.contact.prenom} ${conversation.contact.nom}`.toLowerCase()
          : '';
        const apercu = conversation.dernierMessage?.texte.toLowerCase() ?? '';
        return nomContact.includes(q) || apercu.includes(q);
      }
      return true;
    });
  }, [conversations, filtre, recherche]);

  const handleEnvoyer = (): void => {
    const texte = texteMessage.trim();
    if ((!texte && !fichierSelectionne) || !conversationActiveId) return;
    const fichier = fichierSelectionne;
    const fil = conversationActiveId;
    setRefusEnvoi(null);
    envoyerMessage.mutate(
      { texte, fichier },
      {
        // Le texte refusé revient dans la saisie pour être reformulé, sauf si
        // un autre a déjà été commencé entre-temps.
        onError: (erreur) => {
          if (filOuvertRef.current !== fil) {
            toast(messageErreur(erreur, "Le message n'a pas pu être envoyé"));
            return;
          }
          setTexteMessage((actuel) => actuel || texte);
          setFichierSelectionne((actuel) => actuel ?? fichier);
          setRefusEnvoi(messageErreur(erreur, "Le message n'a pas pu être envoyé"));
        },
      },
    );
    setTexteMessage('');
    setFichierSelectionne(null);
  };

  const lancerAppel = (type: TypeAppel): void => {
    if (!conversationActiveId || !conversationActive?.contact) return;
    if (!contactAppelable(conversationActive.contact)) {
      toast('Utilisateur indisponible pour le moment. Veuillez réessayer lorsque celui-ci sera présent.');
      return;
    }
    appeler(conversationActiveId, conversationActive.contact, type);
  };

  const choisirFichier = (fichier: File | null): void => {
    if (!fichier) return;
    if (fichier.size > TAILLE_MAX_PIECE_JOINTE_OCTETS) {
      toast('Fichier trop volumineux (10 Mo maximum)');
      return;
    }
    setFichierSelectionne(fichier);
  };

  const demarrerConversation = (contact: ContactMessagerie): void => {
    creerConversation.mutate(contact.id, {
      onSuccess: (reponse) => {
        setConversationActiveId(reponse.id);
        setPickerOuvert(false);
        setRechercheContact('');
      },
      onError: (erreur) => toast(messageErreur(erreur, 'Impossible de démarrer la conversation')),
    });
  };

  const contactsFiltres = contacts.filter((contact) => {
    if (!rechercheContact.trim()) return true;
    const q = rechercheContact.trim().toLowerCase();
    return `${contact.prenom} ${contact.nom}`.toLowerCase().includes(q);
  });

  return (
    <div className="mx-auto max-w-[1500px] p-4 sm:p-6">
      <div className="overflow-hidden rounded-2xl bg-white shadow-card">
        <div className="grid grid-cols-1 h-[calc(100vh-180px)] lg:grid-cols-4">
          {/* Sidebar : conversations */}
          <div className="flex flex-col border-r border-gray-100 lg:col-span-1 min-w-0">
            <div className="border-b border-gray-100 p-4">
              <div className="mb-3 flex items-center justify-between">
                <h1 className="font-display text-lg font-bold text-codialis-navy">Messagerie</h1>
                <button
                  type="button"
                  onClick={() => setPickerOuvert(true)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg bg-codialis-navy text-white hover:bg-codialis-deep"
                  title="Nouveau message"
                >
                  <svg aria-hidden="true" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>
              <div className="relative">
                <svg aria-hidden="true"
                  className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-codialis-gray"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="search"
                  aria-label="Rechercher une conversation"
                  value={recherche}
                  onChange={(e) => setRecherche(e.target.value)}
                  placeholder="Rechercher..."
                  className="w-full rounded-lg bg-gray-50 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-codialis-mint"
                />
              </div>
            </div>
            <div className="flex gap-1 px-4 py-2 text-xs">
              <button
                type="button"
                onClick={() => setFiltre('tous')}
                aria-pressed={filtre === 'tous'}
                className={`rounded px-2.5 py-1 ${
                  filtre === 'tous' ? 'bg-codialis-navy text-white' : 'text-codialis-gray hover:bg-gray-100'
                }`}
              >
                Tous
              </button>
              <button
                type="button"
                onClick={() => setFiltre('non_lus')}
                aria-pressed={filtre === 'non_lus'}
                className={`rounded px-2.5 py-1 ${
                  filtre === 'non_lus' ? 'bg-codialis-navy text-white' : 'text-codialis-gray hover:bg-gray-100'
                }`}
              >
                Non lus
                {nonLuesCount > 0 && (
                  <span className="badge-warning ml-1 rounded px-1 text-[9px]">{nonLuesCount}</span>
                )}
              </button>
            </div>
            <div className="scrollbar-thin min-w-0 flex-1 overflow-y-auto">
              {chargementConversations && (
                <p className="p-4 text-center text-xs text-codialis-gray">Chargement…</p>
              )}
              {!chargementConversations && conversationsFiltrees.length === 0 && (
                <p className="p-4 text-center text-xs text-codialis-gray">
                  {conversations.length === 0
                    ? 'Aucune conversation. Démarrez-en une avec le bouton +.'
                    : 'Aucune conversation ne correspond'}
                </p>
              )}
              {conversationsFiltrees.map((conversation) => (
                <ElementConversation
                  key={conversation.id}
                  conversation={conversation}
                  active={conversation.id === conversationActiveId}
                  onSelectionner={() => setConversationActiveId(conversation.id)}
                />
              ))}
            </div>
          </div>

          {/* Fil actif */}
          <div className="flex min-h-0 flex-col lg:col-span-3 min-w-0">
            {!conversationActive && (
              <div className="flex min-w-0 flex-1 items-center justify-center p-4 sm:p-6 text-sm text-codialis-gray">
                {conversations.length === 0
                  ? 'Démarrez une conversation pour commencer à échanger.'
                  : 'Sélectionnez une conversation'}
              </div>
            )}

            {conversationActive && (
              <>
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-100 p-4">
                  <div className="flex items-center gap-3">
                    <div className="relative shrink-0">
                      <div
                        className={`flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br font-bold text-white ${
                          conversationActive.contact ? gradientPour(conversationActive.contact.id) : 'from-gray-400 to-gray-500'
                        }`}
                      >
                        {conversationActive.contact?.initiales ?? '?'}
                      </div>
                      {conversationActive.contact && (
                        <BadgePresence
                          presence={conversationActive.contact.presence}
                          className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5"
                        />
                      )}
                    </div>
                    <div>
                      <div className="font-display font-bold text-codialis-navy">
                        {conversationActive.contact
                          ? `${conversationActive.contact.prenom} ${conversationActive.contact.nom}`
                          : 'Personne indisponible'}
                      </div>
                      <div className="text-xs text-codialis-gray">
                        {conversationActive.contact?.libelleProfil}
                        {conversationActive.contact && (
                          <> · {LIBELLE_PRESENCE[etatPresence(conversationActive.contact.presence)]}</>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => lancerAppel('audio')}
                      disabled={!conversationActive.contact || !contactAppelable(conversationActive.contact)}
                      className="rounded-lg p-2 text-codialis-gray hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                      title={
                        conversationActive.contact && !contactAppelable(conversationActive.contact)
                          ? 'Indisponible pour le moment'
                          : 'Appel audio'
                      }
                    >
                      <svg aria-hidden="true" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => lancerAppel('video')}
                      disabled={!conversationActive.contact || !contactAppelable(conversationActive.contact)}
                      className="rounded-lg p-2 text-codialis-gray hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                      title={
                        conversationActive.contact && !contactAppelable(conversationActive.contact)
                          ? 'Indisponible pour le moment'
                          : 'Appel visio'
                      }
                    >
                      <svg aria-hidden="true" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Messages */}
                <div className="scrollbar-thin min-h-0 min-w-0 flex-1 space-y-4 overflow-y-auto bg-gray-50/40 p-4 sm:p-6">
                  {chargementMessages && (
                    <p className="text-center text-xs text-codialis-gray">Chargement…</p>
                  )}
                  {!chargementMessages && messages.length === 0 && (
                    <p className="text-center text-xs text-codialis-gray">
                      Aucun message pour l'instant. Écrivez le premier !
                    </p>
                  )}

                  {messages.map((m, idx) => {
                    const precedent = messages[idx - 1];
                    const nouveauJour = !precedent || jourEnFrancais(precedent.envoyeLe) !== jourEnFrancais(m.envoyeLe);

                    return (
                      <div key={m.id}>
                        {nouveauJour && (
                          <div className="mb-4 text-center text-xs text-codialis-gray">
                            {jourEnFrancais(m.envoyeLe)}
                          </div>
                        )}
                        {m.type === 'appel' && m.appel ? (
                          <LogAppel appel={m.appel} deMoi={m.deMoi} heure={heure(m.envoyeLe)} />
                        ) : m.deMoi ? (
                          <div className="flex justify-end">
                            <div className="max-w-md">
                              <div className="ai-msg-user space-y-2 p-3 text-sm">
                                {m.texte && <TexteMessage texte={m.texte} />}
                                {m.pieceJointe && conversationActiveId && (
                                  <CartePieceJointe
                                    pieceJointe={m.pieceJointe}
                                    url={urlPieceJointeMessage(conversationActiveId, m.id)}
                                    urlApercu={urlPieceJointeMessage(conversationActiveId, m.id, { inline: true })}
                                    onAgrandir={() =>
                                      setPieceJointeAgrandie({
                                        pieceJointe: m.pieceJointe!,
                                        url: urlPieceJointeMessage(conversationActiveId, m.id),
                                        urlApercu: urlPieceJointeMessage(conversationActiveId, m.id, { inline: true }),
                                      })
                                    }
                                    sombre
                                  />
                                )}
                              </div>
                              <div className="mt-1 text-right text-[10px] text-codialis-gray">
                                {heure(m.envoyeLe)}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="flex gap-3">
                            <div
                              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br text-xs font-bold text-white ${gradientPour(m.expediteur.id)}`}
                            >
                              {m.expediteur.initiales}
                            </div>
                            <div className="max-w-md">
                              <div className="space-y-2 rounded-2xl rounded-tl-sm border border-gray-100 bg-white p-3 text-sm text-codialis-navy shadow-sm">
                                {m.texte && <TexteMessage texte={m.texte} />}
                                {m.pieceJointe && conversationActiveId && (
                                  <CartePieceJointe
                                    pieceJointe={m.pieceJointe}
                                    url={urlPieceJointeMessage(conversationActiveId, m.id)}
                                    urlApercu={urlPieceJointeMessage(conversationActiveId, m.id, { inline: true })}
                                    onAgrandir={() =>
                                      setPieceJointeAgrandie({
                                        pieceJointe: m.pieceJointe!,
                                        url: urlPieceJointeMessage(conversationActiveId, m.id),
                                        urlApercu: urlPieceJointeMessage(conversationActiveId, m.id, { inline: true }),
                                      })
                                    }
                                  />
                                )}
                              </div>
                              <div className="mt-1 text-[10px] text-codialis-gray">{heure(m.envoyeLe)}</div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div ref={finDesMessagesRef} />
                </div>

                {/* Composer */}
                <div className="border-t border-gray-100 p-4">
                  {refusEnvoi && (
                    <div className="mb-2">
                      <MessageErreur message={refusEnvoi} />
                    </div>
                  )}
                  {fichierSelectionne && (
                    <div className="mb-2 flex items-center gap-2 rounded-xl bg-gray-50 px-3 py-2 text-xs text-codialis-navy">
                      <Icone nom="trombone" className="h-4 w-4 shrink-0 text-codialis-gray" />
                      <span className="truncate font-medium">{fichierSelectionne.name}</span>
                      <span className="shrink-0 text-codialis-gray">
                        {tailleLisible(fichierSelectionne.size)}
                      </span>
                      <button
                        aria-label="Retirer la pièce jointe"
                        type="button"
                        onClick={() => setFichierSelectionne(null)}
                        className="ml-auto shrink-0 rounded p-1 text-codialis-gray hover:bg-gray-200 hover:text-codialis-navy"
                        title="Retirer la pièce jointe"
                      >
                        <Icone nom="croix" className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                  <div className="flex items-end gap-2 rounded-2xl bg-gray-50 p-2">
                    <input
                      ref={inputFichierRef}
                      type="file"
                      accept={ACCEPT_PIECE_JOINTE}
                      hidden
                      onChange={(e) => {
                        choisirFichier(e.target.files?.[0] ?? null);
                        e.target.value = '';
                      }}
                    />
                    <button
                      aria-label="Joindre un fichier (PDF, JPG, PNG — 10 Mo max)"
                      type="button"
                      onClick={() => inputFichierRef.current?.click()}
                      className="p-2 text-codialis-gray hover:text-codialis-navy"
                      title="Joindre un fichier (PDF, JPG, PNG — 10 Mo max)"
                    >
                      <Icone nom="trombone" className="h-5 w-5" />
                    </button>
                    <textarea
                      aria-label="Votre message"
                      value={texteMessage}
                      onChange={(e) => {
                        setRefusEnvoi(null);
                        setTexteMessage(e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleEnvoyer();
                        }
                      }}
                      placeholder="Tapez votre message…"
                      rows={1}
                      className="min-w-0 flex-1 resize-none bg-transparent py-2.5 text-sm focus:outline-none"
                    />
                    <button
                      aria-label="Envoyer"
                      type="button"
                      onClick={handleEnvoyer}
                      disabled={(!texteMessage.trim() && !fichierSelectionne) || envoyerMessage.isPending}
                      className="flex h-10 w-10 items-center justify-center rounded-xl bg-codialis-navy text-white transition hover:bg-codialis-deep disabled:opacity-50"
                      title="Envoyer"
                    >
                      <svg aria-hidden="true" className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M3.105 3.105a1.5 1.5 0 011.6-.354l11.997 4.5a1.5 1.5 0 010 2.798l-12 4.5a1.5 1.5 0 01-1.953-1.953l1.6-4.272a.5.5 0 01.4-.32l7.5-.937a.5.5 0 000-.99l-7.5-.937a.5.5 0 01-.4-.32L2.752 5.058a1.5 1.5 0 01.353-1.953z" />
                      </svg>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <Modale
        titre="Nouveau message"
        sousTitre="Choisissez un ou une destinataire"
        ouverte={pickerOuvert}
        onFermer={() => setPickerOuvert(false)}
      >
        <div className="p-4 sm:p-6">
          <input
            autoFocus
            type="search"
            aria-label="Rechercher une personne"
            value={rechercheContact}
            onChange={(e) => setRechercheContact(e.target.value)}
            placeholder="Rechercher une personne..."
            className="mb-3 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-codialis-mint"
          />
          <div className="max-h-80 space-y-1 overflow-y-auto">
            {contactsFiltres.length === 0 && (
              <p className="py-6 text-center text-sm text-codialis-gray">Aucune personne trouvée</p>
            )}
            {contactsFiltres.map((contact) => (
              <button
                key={contact.id}
                type="button"
                disabled={creerConversation.isPending}
                onClick={() => demarrerConversation(contact)}
                className="flex w-full items-center gap-3 rounded-xl p-2.5 text-left transition hover:bg-gray-50 disabled:opacity-50"
              >
                <div className="relative shrink-0">
                  <div
                    className={`flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br text-xs font-bold text-white ${gradientPour(contact.id)}`}
                  >
                    {contact.initiales}
                  </div>
                  <BadgePresence presence={contact.presence} className="absolute -bottom-0.5 -right-0.5 h-3 w-3" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-codialis-navy">
                    {contact.prenom} {contact.nom}
                  </div>
                  <div className="truncate text-xs text-codialis-gray">{contact.libelleProfil}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </Modale>

      <Modale
        titre={pieceJointeAgrandie?.pieceJointe.nom ?? ''}
        sousTitre={pieceJointeAgrandie ? tailleLisible(pieceJointeAgrandie.pieceJointe.tailleOctets) : undefined}
        ouverte={pieceJointeAgrandie !== null}
        onFermer={() => setPieceJointeAgrandie(null)}
        largeur="max-w-4xl"
      >
        {pieceJointeAgrandie && (
          <div className="space-y-3 px-4 py-4 sm:px-6">
            <div className="relative flex min-h-[40vh] items-center justify-center overflow-auto rounded-xl bg-codialis-ink p-2">
              {estImage(pieceJointeAgrandie.pieceJointe.typeMime) && (
                <img
                  src={pieceJointeAgrandie.urlApercu}
                  alt={pieceJointeAgrandie.pieceJointe.nom}
                  className="max-h-[70vh] w-auto max-w-full rounded-lg object-contain"
                />
              )}
              {estVideo(pieceJointeAgrandie.pieceJointe.typeMime) && (
                <video
                  src={pieceJointeAgrandie.urlApercu}
                  controls
                  autoPlay
                  className="max-h-[70vh] w-auto max-w-full rounded-lg"
                />
              )}
            </div>
            <div className="flex justify-end">
              <a
                href={pieceJointeAgrandie.url}
                className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
              >
                <Icone nom="telecharger" className="h-4 w-4" />
                Télécharger
              </a>
            </div>
          </div>
        )}
      </Modale>
    </div>
  );
}

/** Log d'appel dans le fil, à la Discord : ligne centrée neutre, pas une bulle. */
function LogAppel({ appel, deMoi, heure: heureAffichee }: { appel: AppelMessage; deMoi: boolean; heure: string }): JSX.Element {
  const echoue =
    appel.statut === 'MANQUE' ||
    appel.statut === 'REFUSE' ||
    appel.statut === 'ECHOUE' ||
    appel.statut === 'INDISPONIBLE';
  return (
    <div className="flex justify-center">
      <div
        className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs ${
          echoue ? 'border-red-100 bg-red-50 text-red-600' : 'border-gray-100 bg-white text-codialis-gray'
        }`}
      >
        {appel.type === 'audio' ? (
          <svg aria-hidden="true" className="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
        ) : (
          <svg aria-hidden="true" className="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        )}
        <span>{libelleAppel(appel, deMoi)}</span>
        <span className="text-[10px] text-codialis-gray">{heureAffichee}</span>
      </div>
    </div>
  );
}

const REGEX_URL = /(https?:\/\/[^\s]+)/g;

/**
 * Rend un lien d'appel/visio cliquable dans un message texte, sans casser le reste.
 * `split` avec un groupe capturant intercale les URL trouvées aux index impairs.
 */
function TexteMessage({ texte }: { texte: string }): JSX.Element {
  const segments = texte.split(REGEX_URL);
  return (
    <p className="whitespace-pre-wrap break-words">
      {segments.map((segment, index) =>
        index % 2 === 1 ? (
          <a
            key={index}
            href={segment}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 hover:opacity-80"
          >
            {segment}
          </a>
        ) : (
          <span key={index}>{segment}</span>
        ),
      )}
    </p>
  );
}

function CartePieceJointe({
  pieceJointe,
  url,
  urlApercu,
  onAgrandir,
  sombre = false,
}: {
  pieceJointe: PieceJointeMessage;
  /** Lien de téléchargement (Content-Disposition: attachment). */
  url: string;
  /** Lien d'aperçu (Content-Disposition: inline), pour l'image ou la vidéo. */
  urlApercu: string;
  onAgrandir: () => void;
  sombre?: boolean;
}): JSX.Element {
  if (estImage(pieceJointe.typeMime) || estVideo(pieceJointe.typeMime)) {
    return (
      <div className="group relative h-40 w-56 max-w-full overflow-hidden rounded-xl bg-black/5">
        {estImage(pieceJointe.typeMime) ? (
          <img
            src={urlApercu}
            alt={pieceJointe.nom}
            className="h-full w-full cursor-pointer object-cover"
            onClick={onAgrandir}
          />
        ) : (
          <video
            src={`${urlApercu}#t=0.1`}
            muted
            preload="metadata"
            className="h-full w-full cursor-pointer object-cover"
            onClick={onAgrandir}
          />
        )}

        {estVideo(pieceJointe.typeMime) && (
          <div
            className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/20"
            aria-hidden="true"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/90">
              <IconePleine nom="lecturePleine" className="h-4 w-4 translate-x-0.5 text-codialis-navy" />
            </span>
          </div>
        )}

        {/* Icônes latérales : plein écran et téléchargement, toujours visibles
            sur tablette (tactile), révélées au survol sur desktop. */}
        <div className="absolute right-1.5 top-1.5 flex flex-col gap-1 opacity-100 transition sm:opacity-0 sm:group-hover:opacity-100">
          <button
            aria-label="Plein écran"
            type="button"
            onClick={onAgrandir}
            title="Plein écran"
            className="flex h-7 w-7 items-center justify-center rounded-lg bg-black/50 text-white hover:bg-black/70"
          >
            <Icone nom="pleinEcran" className="h-3.5 w-3.5" />
          </button>
          <a aria-label="Télécharger"
            href={url}
            title="Télécharger"
            className="flex h-7 w-7 items-center justify-center rounded-lg bg-black/50 text-white hover:bg-black/70"
          >
            <Icone nom="telecharger" className="h-3.5 w-3.5" />
          </a>
        </div>

        <div className="absolute bottom-0 left-0 right-0 truncate bg-gradient-to-t from-black/60 to-transparent px-2 py-1 text-[10px] font-medium text-white">
          {pieceJointe.nom} · {tailleLisible(pieceJointe.tailleOctets)}
        </div>
      </div>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`flex items-center gap-2 rounded-xl border p-2 text-xs transition hover:opacity-80 ${
        sombre
          ? 'border-white/20 bg-white/10 text-white'
          : 'border-gray-100 bg-gray-50 text-codialis-navy'
      }`}
    >
      <Icone nom="document" className="h-4 w-4 shrink-0" />
      <span className="min-w-0 flex-1 truncate font-medium">{pieceJointe.nom}</span>
      <span className={`shrink-0 ${sombre ? 'text-white/70' : 'text-codialis-gray'}`}>
        {tailleLisible(pieceJointe.tailleOctets)}
      </span>
      <Icone nom="telecharger" className="h-4 w-4 shrink-0" />
    </a>
  );
}

function ElementConversation({
  conversation,
  active,
  onSelectionner,
}: {
  conversation: ConversationResume;
  active: boolean;
  onSelectionner: () => void;
}): JSX.Element {
  const contact = conversation.contact;
  const nonLu = conversation.nonLus > 0;

  return (
    <button
      type="button"
      onClick={onSelectionner}
      aria-current={active ? 'true' : undefined}
      className={`flex w-full items-start gap-3 p-4 text-left transition ${
        active
          ? 'border-l-4 border-codialis-mint bg-codialis-mint/5'
          : 'border-l-4 border-transparent hover:bg-gray-50'
      }`}
    >
      <div className="relative shrink-0">
        <div
          className={`flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br text-xs font-bold text-white ${
            contact ? gradientPour(contact.id) : 'from-gray-400 to-gray-500'
          }`}
        >
          {contact?.initiales ?? '?'}
        </div>
        {nonLu && (
          <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-white bg-codialis-mint" />
        )}
        {contact && <BadgePresence presence={contact.presence} className="absolute -bottom-0.5 -right-0.5 h-3 w-3" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className={`truncate text-sm ${nonLu ? 'font-bold' : 'font-medium'} text-codialis-navy`}>
            {contact ? `${contact.prenom} ${contact.nom}` : 'Personne indisponible'}
          </span>
          <span className="shrink-0 text-[10px] text-codialis-gray">
            {dateEtHeureCourtes(conversation.dernierMessageLe)}
          </span>
        </div>
        <div className="text-[10px] text-codialis-gray">{contact?.libelleProfil}</div>
        <div
          className={`mt-0.5 flex items-center gap-1 truncate text-xs ${nonLu ? 'font-medium text-codialis-navy' : 'text-codialis-gray'}`}
        >
          {conversation.dernierMessage ? (
            conversation.dernierMessage.appel ? (
              <span className="truncate">
                {libelleAppel(conversation.dernierMessage.appel, conversation.dernierMessage.deMoi)}
              </span>
            ) : (
              <>
                {conversation.dernierMessage.deMoi && 'Vous : '}
                {conversation.dernierMessage.aPieceJointe && (
                  <Icone nom="trombone" className="h-3 w-3 shrink-0" />
                )}
                <span className="truncate">
                  {conversation.dernierMessage.texte ||
                    (conversation.dernierMessage.aPieceJointe ? 'Pièce jointe' : '')}
                </span>
              </>
            )
          ) : (
            'Aucun message'
          )}
        </div>
      </div>
    </button>
  );
}
